import { Table, Typography, Tooltip, Button, Space, TimePicker } from 'antd'
import { EditOutlined, SaveOutlined, CloseOutlined, CheckOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { useState } from 'react'

const { Text } = Typography

const WorkReportFlatTable = ({ tableBody = [], editedTimeLogs, setEditedTimeLogs }) => {
    const [editingRowId, setEditingRowId] = useState(null)
    const [editedTimes, setEditedTimes] = useState({})

    const safeArray = Array.isArray(tableBody) ? tableBody : [tableBody]

    const flatData = safeArray.flatMap((row) =>
        (row?.list || []).map((task) => ({
            ...task,
            startLogDate: row._id?.startLogDate,
            assigneeId: row._id?.assigneeId,
        }))
    )

    const updateEditedTimeLogs = (record, newStart, newEnd) => {
        const updatedTableBody = editedTimeLogs?.tableBody?.map((userData) => {
            if (userData._id?.assigneeId !== record.assigneeId) return userData

            return {
                ...userData,
                list: userData.list.map((logByDate) => {
                    if (logByDate._id?.startLogDate !== record.startLogDate) return logByDate

                    let addedDurationInMs = 0

                    const updatedTasks = logByDate.list.map((task) => {
                        if (
                            task._id === record._id &&
                            task.timelogs._id === record.timelogs._id
                        ) {
                            const oldStart = task.timelogs.start
                            const oldEnd = task.timelogs.end || oldStart
                            const oldDuration = oldEnd - oldStart
                            const newDuration = newEnd - newStart
                            addedDurationInMs = newDuration - oldDuration

                            return {
                                ...task,
                                timelogs: {
                                    ...task.timelogs,
                                    start: newStart,
                                    end: newEnd,
                                },
                            }
                        }
                        return task
                    })

                    return {
                        ...logByDate,
                        list: updatedTasks,
                        totalLog: {
                            ...logByDate.totalLog,
                            total: logByDate.totalLog.total + addedDurationInMs,
                        },
                    }
                }),
            }
        })

        setEditedTimeLogs({
            ...editedTimeLogs,
            tableBody: updatedTableBody,
        })
    }



    const columns = [
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            width: 150,
            ellipsis: { showTitle: false },
            render: (text) => (
                <Tooltip title={text}>
                    <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {text}
                    </div>
                </Tooltip>
            ),
        },
        {
            title: 'Estimation',
            dataIndex: 'estimation',
            key: 'estimation',
            width: 100,
            render: (val) => (val ? `${val} min` : '0'),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 100,
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            width: 100,
        },
        {
            title: 'Time Logged',
            key: 'timeLogged',
            width: 250,
            render: (_, record) => {
                const { start, end } = record?.timelogs || {}
                const isEditing = editingRowId === record.timelogs._id
                if (isEditing) {
                    return (
                        <Space>
                            <TimePicker
                                format="HH:mm:ss"
                                value={editedTimes[record.timelogs._id]}
                                onChange={(val) =>
                                    setEditedTimes((prev) => ({
                                        ...prev,
                                        [record.timelogs._id]: val,
                                    }))
                                }
                            />
                            <Button
                                icon={<CheckOutlined style={{ color: "green" }} />}
                                type="primary"
                                size="small"
                                style={{ backgroundColor: "transparent", border: "none" }}
                                onClick={() => {
                                    const picked = editedTimes[record.timelogs._id]
                                    if (!picked) return
                                    const newStart = record.timelogs.start
                                    const addedDurationInMs =
                                        (picked.hour() * 3600 +
                                            picked.minute() * 60 +
                                            picked.second()) * 1000

                                    const existingDuration = (record.timelogs.end || Date.now()) - newStart
                                    const newEnd = newStart + addedDurationInMs
                                    updateEditedTimeLogs(record, newStart, newEnd)
                                    setEditingRowId(null)
                                }}
                            />
                            <Button
                                icon={<CloseOutlined style={{ color: "red", fontSize: "15px" }} />}
                                size="small"
                                onClick={() => setEditingRowId(null)}
                                style={{ backgroundColor: "transparent", border: "none" }}
                            />
                        </Space>
                    )
                }
                let timeStr = '-'
                let isOngoing = false

                if (start) {
                    const actualEnd = end || Date.now()
                    const duration = actualEnd - start

                    const h = Math.floor(duration / 3600000)
                    const m = Math.floor((duration % 3600000) / 60000)
                    const s = Math.floor((duration % 60000) / 1000)

                    timeStr = `${h}h ${m}m ${s}s`
                    isOngoing = !end
                }

                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>{timeStr}</span>
                        {isOngoing && (
                            <Tooltip title="This task is currently ongoing">
                                <span style={{
                                    backgroundColor: '#fadb14',
                                    color: '#000',
                                    fontSize: '12px',
                                    padding: '2px 6px',
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                }}>
                                    Ongoing
                                </span>
                            </Tooltip>
                        )}
                        <Tooltip title="Edit Time">
                            <Button
                                type="text"
                                icon={<EditOutlined style={{ color: "#000" }} />}
                                size="small"
                                onClick={() => setEditingRowId(record.timelogs._id)}
                            />
                        </Tooltip>
                    </div>
                )

            },
        },
    ]

    return (
        <Table
            columns={columns}
            dataSource={flatData}
            rowKey={(record) => record.timelogs._id}
            pagination={false}
            size="small"
            bordered
        />
    )
}

export default WorkReportFlatTable
